import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel } from "../types";

const syncLipsyncV2ProParams = z
  .object({
    syncMode: z
      .enum(["cut_off", "loop", "bounce", "silence", "remap"])
      .default("cut_off"),
    videoDurationSeconds: z.number().int().min(1).max(600).default(60),
    attachments: z.array(attachment).optional(),
  })
  .strip();

export const syncLipsyncV2Pro = defineModel({
  key: "sync-lipsync-v2-pro",
  stepType: "video",
  spec: {
    label: "Lip Sync",
    description: "Sync lips in a video to a new audio track",
    icon: "swap-audio-icon.svg",
    tags: ["video-edit"],
    featured: true,
    info: "Pricing: $6/min.",
    features: {
      attachments: true,
      syncMode: true,
    } as const,
    options: {
      syncMode: {
        items: [
          { label: "Cut off", value: "cut_off" },
          { label: "Loop", value: "loop" },
          { label: "Bounce", value: "bounce" },
          { label: "Silence", value: "silence" },
          { label: "Remap", value: "remap" },
        ],
      },
      attachments: [
        {
          type: "video",
          allowedFormats: ["video/mp4", "video/webm", "video/quicktime"],
          label: "Video",
          slot: "video",
          required: true,
        },
        {
          type: "audio",
          allowedFormats: [
            "audio/mpeg",
            "audio/mp3",
            "audio/wav",
            "audio/x-wav",
            "audio/webm",
            "audio/aac",
            "audio/mp4",
          ],
          label: "Audio",
          slot: "audio",
          required: true,
        },
      ],
    },
    params: syncLipsyncV2ProParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/sync-lipsync/v2/pro",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 83_333 }],
  },
  providers: [
    {
      provider: "fal",
      modelId: "fal-ai/sync-lipsync/v2/pro",
      costPerSecond: (5 / 60) * 1_000_000 * (1 + BRAND_MARKUP),
      estimateCost(params) {
        const seconds = Number(params.videoDurationSeconds ?? 60);
        return Math.max(1, seconds) * (5 / 60) * 1_000_000 * (1 + BRAND_MARKUP);
      },
      toPayload(params) {
        const videoUrl = params.attachments?.find(
          (item) => item.slot === "video",
        )?.data;
        const audioUrl = params.attachments?.find(
          (item) => item.slot === "audio",
        )?.data;

        if (!videoUrl) {
          throw new ValidationError("Video is required for lip sync.");
        }
        if (!audioUrl) {
          throw new ValidationError("Audio is required for lip sync.");
        }

        return {
          model: this.modelId,
          video_url: videoUrl,
          audio_url: audioUrl,
          sync_mode: params.syncMode ?? "cut_off",
        };
      },
    },
  ],
});
